// Sample GAPP packet data — one source for the /doc-preview dev page and the
// Node verify script (scripts/verify-gapp-full.mjs). Every field the overlay
// places is populated so each appendix can be checked against its original.

import { PACKET_DEFS } from './packets';
import { buildParties } from './packetParties';
import { normalizeLang, formTitle, packetLabel, signerLabel } from './i18n';

// GAPP sample — every field the overlay places, so each appendix can be checked
// against the blank original.
const sig = (name, d = '05/29/2026') => ({ name, date: 'May 29, 2026', dateShort: d });
const GAPP_SAMPLE = {
  date: 'May 30, 2026', dateShort: '05/30/2026',
  packet: { id: '7f3a9c2e-sample', type: 'gapp_intake', label: 'GAPP Unified Intake Packet', createdAt: '2026-05-28T14:02:00Z', completedAt: '2026-05-30T16:45:00Z' },
  client: {
    name: 'Marcus Johnson', id: 'G041', dob: '03/14/2012', age: '14', medicaid: '111222333A',
    guardian: 'Sandra Johnson', coordinator: 'Grace Olowu', phone: '(678) 555-0174',
    relationship: 'Mother', email: 'sjohnson@email.com',
    address: '245 Maple Court, Canton, GA 30114',
  },
  forms: {
    phiReleased: ['Medical history/diagnoses', 'Physician orders/POC', 'Nursing notes', 'Medications', 'GAPP documentation'],
    phiValid: 'Until end of home care services', phiSignerType: 'Parent / Legal Guardian',
    phiPrintedName: 'Sandra Johnson', phiRelationship: 'Mother',
    nacpVisitDate: '05/27/2026', nacpRnLicense: 'RN123456',
    nAddress: '245 Maple Court, Canton, GA 30114', nDfcsCounty: 'N/A', nDfcsAddress: '', nDfcsSupervisor: '',
    nPhysician: 'Dr. Kevin Patel — 112 Medical Plaza Dr, Canton, GA 30114', nAdmission: 'New Admission',
    nChecklist: ['Physician Plan of Treatment - Appendix T', 'Letter of Understanding (yearly)', 'Freedom of Choice Form (yearly)',
      'Current IEP (if there is one)', 'Attestation for Transfers and Shared Hours - Appendix U',
      'Medical Necessity Statement for Admission or Continued Stay', 'Seizure Logs (if requesting hours for seizure activity)',
      'Nursing Care Plan', 'Supervisory Visit Notes'],
    nAgencyRep: 'Grace Olowu', nPacketDate: '05/30/2026',
    hWeight: '72 lbs', hDfcsCustody: 'No', hDfcsWorker: 'N/A', hSkilledHrs: '56/wk', hPssHrs: '0',
    hOtherMedicaid: 'No', hServicesProvided: 'Private duty nursing (GAPP)', hFamilyPaid: 'No', hInSchool: 'Yes', hSchoolHrs: '6', hNurseSchool: 'No',
    hIep: 'Yes', hRationale: '', hPrivateIns: 'No', hOtherGapp: 'No', hOtherGappNames: '', hCompetent: 'Yes', hPa: 'DMA80-2026-04421',
    sCurrentHrs: '56 hrs/week', sAgency1Name: 'A Heart For Care', sAgency1Contact: 'Grace Olowu / 470-470-6640',
    sAgency1Hrs: '40 hrs/week', sAgency2Name: 'Sunshine Home Health', sAgency2Contact: 'J. Carter / 770-555-0192',
    sAgency2Hrs: '16 hrs/week', sPaExp: '12/31/2026', sSharedStart: '06/01/2026', sCaregiverPhone: '(678) 555-0174',
    vSeizures: [
      { date: '05/12/2026', time: '3:14 PM', duration: '45 sec', type: 'Tonic-clonic', intervention: 'Repositioned, timed, O2 sat monitored', response: 'Post-ictal 10 min, recovered', reporter: 'Sandra Johnson' },
      { date: '05/19/2026', time: '7:02 AM', duration: '30 sec', type: 'Absence', intervention: 'Observed', response: 'Returned to baseline', reporter: 'R. Adeola, RN' },
    ],
    rTransferDate: '05/30/2026', rCurrentAgency: 'A Heart For Care', rCurrentContact: 'Grace Olowu / 470-470-6640',
    rHrsApproved: '56 hrs/week', rPaExp: '12/31/2026', rSubmissionDate: '05/30/2026',
    rReason: 'Family is relocating to Cherokee County and prefers a provider closer to their new residence so that the same nurses can continue without long commutes.',
    rReceivingAgency: 'Sunshine Home Health LLC', rDma80Date: '05/30/2026', rCaregiverPhone: '(678) 555-0174',
  },
  signatures: {
    phi_rep: sig('Sandra Johnson'), phi_witness: sig('Grace Olowu', '05/30/2026'),
    nacp_parent: sig('Sandra Johnson'), nacp_rn: sig('Rosemary Adeola', '05/30/2026'),
    i_caregiver: sig('Sandra Johnson'), i_agency: sig('Grace Olowu', '05/30/2026'),
    g_provider: sig('Grace Olowu', '05/30/2026'), g_member: sig('Sandra Johnson'), g_caregiver: sig('Sandra Johnson'), g_witness: sig('Grace Olowu', '05/30/2026'),
    h_caregiver: sig('Sandra Johnson'), h_agency: sig('Grace Olowu', '05/30/2026'),
    u_caregiver: sig('Sandra Johnson'), u_agency: sig('Grace Olowu', '05/30/2026'),
    s_caregiver: sig('Sandra Johnson'),
    r_caregiver: sig('Sandra Johnson', '05/30/2026'),
  },
  slotsMeta: [],
};

export function SAMPLE_GAPP_DATA(type, language = 'en') {
  const def = PACKET_DEFS[type];
  const lang = normalizeLang(language);
  const titles = Object.fromEntries(def.forms.map((f) => [f.form_key, f.form_title]));
  const tokens = [{ party_role: 'client', sent_to_email: 'sjohnson@email.com', created_at: '2026-05-29T14:30:00Z', accessed_at: '2026-05-29T18:04:00Z', access_count: 2 }];
  const slotsMeta = def.slots.map((s) => ({
      ...s, form_title: formTitle(lang, s.form_key, titles[s.form_key]),
      signer_label: signerLabel(lang, s.slot_key, s.signer_label),
      signed_name: GAPP_SAMPLE.signatures[s.slot_key]?.name || '',
      signed_at: GAPP_SAMPLE.signatures[s.slot_key] ? (s.party_role === 'client' ? '2026-05-29T18:21:00Z' : '2026-05-30T15:02:00Z') : null,
      signed_by: s.party_role === 'client' ? null : 'staff',
      signed_ip: s.party_role === 'client' ? '73.184.22.109' : null,
    }));
  return {
    ...GAPP_SAMPLE,
    lang,
    packet: { ...GAPP_SAMPLE.packet, type, label: packetLabel(lang, type, def.label) },
    formKeys: def.forms.map((f) => f.form_key),
    slotsMeta,
    parties: buildParties(slotsMeta, tokens),
  };
}

